import { useCallback, useMemo, useState } from "react";
import { MenuAnalyticsMealPeriod, MenuAnalyticsTimeRange, MenuItemsSortField, SortDirection } from "../types";

// Centralized filter / sort / pagination state for the analytics dashboard.
// Lives above the tabs so selections persist when switching between the Menu
// Items and Ingredients tabs. Any filter change resets to page 1;
export interface SharedAnalyticsFilters {
  timeRange: MenuAnalyticsTimeRange;
  startDate?: string;
  endDate?: string;
  mealPeriod: MenuAnalyticsMealPeriod;
  teamId?: string;
}

export interface AnalyticsFilterState extends SharedAnalyticsFilters {
  sortBy: MenuItemsSortField;
  sortDir: SortDirection;
  page: number;
  pageSize: number;
}

const INITIAL: SharedAnalyticsFilters = {
  timeRange: "thisWeek",
  mealPeriod: "all",
};

export interface UseAnalyticsFilters {
  filters: SharedAnalyticsFilters;
  filtersKey: string;
  setTimeRange: (timeRange: MenuAnalyticsTimeRange) => void;
  setCustomRange: (startDate: string, endDate: string) => void;
  setMealPeriod: (mealPeriod: MenuAnalyticsMealPeriod) => void;
  setTeamId: (teamId: string | undefined) => void;
}

export const useAnalyticsFilters = (): UseAnalyticsFilters => {
  const [filters, setFilters] = useState<SharedAnalyticsFilters>(INITIAL);

  const patch = useCallback((next: Partial<SharedAnalyticsFilters>) => setFilters(prev => ({ ...prev, ...next })), []);

  const setTimeRange = useCallback(
    (timeRange: MenuAnalyticsTimeRange) =>
      patch(timeRange === "custom" ? { timeRange } : { timeRange, startDate: undefined, endDate: undefined }),
    [patch]
  );

  const setCustomRange = useCallback(
    (startDate: string, endDate: string) => patch({ timeRange: "custom", startDate, endDate }),
    [patch]
  );

  const setMealPeriod = useCallback((mealPeriod: MenuAnalyticsMealPeriod) => patch({ mealPeriod }), [patch]);
  const setTeamId = useCallback((teamId: string | undefined) => patch({ teamId }), [patch]);

  const filtersKey = JSON.stringify(filters);

  return useMemo(
    () => ({ filters, filtersKey, setTimeRange, setCustomRange, setMealPeriod, setTeamId }),
    [filters, filtersKey, setTimeRange, setCustomRange, setMealPeriod, setTeamId]
  );
};
